/**
 * OT-0F.1 — Accreditation Evidence Drilldown modal.
 *
 * Renders ONLY patient-safe metadata. Never exposes raw storage paths,
 * reviewer IDs/emails, owner IDs, or document blobs. If no public-safe
 * `evidence` payload is attached, shows the compliance review fallback.
 */
import React, { useState } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Award, ShieldCheck, Calendar, Building2, CheckCircle2, Info, FileText, ExternalLink, X } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  try { return formatDateLocale(iso); }
  catch (_) { return ''; }
}

// Defensive allow-list. The backend already strips internal keys, but
// we enforce the same allow-list here so a future projection-bug cannot
// silently surface anything we did not approve for patient eyes.
const _ALLOWED_EVIDENCE_KEYS = new Set([
  'issuing_authority',
  'expiry_date',
  'verification_note',
  'thumbnail_url',
  'public_certificate_url',
  'mime_type',
]);

function pickSafeEvidence(evidence) {
  if (!evidence || typeof evidence !== 'object') return null;
  const out = {};
  for (const [k, v] of Object.entries(evidence)) {
    if (_ALLOWED_EVIDENCE_KEYS.has(k) && v) out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

export default function AccreditationEvidenceModal({ open, onClose, accreditation }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const a = accreditation || {};
  const evidence = pickSafeEvidence(a.evidence);
  const issuer = evidence?.issuing_authority;
  const expiry = evidence?.expiry_date;
  const note = evidence?.verification_note;
  const thumbnail = evidence?.thumbnail_url;
  const publicCert = evidence?.public_certificate_url;
  const mime = (evidence?.mime_type || '').toLowerCase();
  const isPdf = mime.includes('pdf') || (publicCert || '').toLowerCase().includes('.pdf');
  const hasVisualEvidence = !!(thumbnail || publicCert);
  const hasAnyPublicEvidence = !!(issuer || expiry || note || hasVisualEvidence);

  return (
    <>
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent
        className="max-w-lg"
        data-testid="accreditation-evidence-modal"
        data-render-authority="TrustEvidenceModal-v1"
        data-render-generation="TRUST-001"
        data-sprint-fingerprint="COM-FOUNDATION"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Award className="h-4 w-4 text-emerald-600" />
            <span data-testid="accreditation-modal-title">{a.long_label || a.short_label || a.slug}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Accreditation evidence details for {a.long_label || a.short_label || a.slug}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 -mt-2 mb-2" data-testid="accreditation-modal-status-row">
          <Badge
            className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] gap-1"
            data-testid="accreditation-modal-status"
          >
            <ShieldCheck className="h-3 w-3" />
            {a.is_verified_new ? 'Verified by TreatX compliance review' : 'Unverified'}
          </Badge>
          {a.short_label && a.short_label !== (a.long_label || a.short_label) && (
            <span className="text-[11px] text-slate-500 font-mono">{a.short_label}</span>
          )}
        </div>

        {/* SEARCH-UX-HARDFIX-02 FIX-1: real certificate visual preview.
            For images we render a clickable thumbnail that opens a
            full-size lightbox.  For PDFs we render an embedded iframe
            (most browsers display PDFs inline) plus an explicit
            "Open in new tab" link.  Both paths source the file from
            the canonical `clinic_verification_documents.file_url` via
            the existing `/api/media/<id>` route — zero new endpoints. */}
        {hasVisualEvidence && (
          <div className="mb-3" data-testid="accreditation-modal-evidence-preview">
            {isPdf && publicCert ? (
              <div className="rounded-md border border-slate-200 overflow-hidden bg-slate-50">
                <object
                  data={publicCert}
                  type="application/pdf"
                  className="w-full h-72 bg-white"
                  aria-label="Accreditation certificate PDF preview"
                >
                  <div className="p-6 text-center text-xs text-slate-600 flex flex-col items-center gap-2">
                    <FileText className="h-8 w-8 text-slate-400" />
                    <p>Preview not available in this browser.</p>
                  </div>
                </object>
                <a
                  href={publicCert}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-50 border-t border-slate-200 py-2"
                  data-testid="accreditation-modal-evidence-open-pdf"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open certificate PDF
                </a>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="group block w-full rounded-md border border-slate-200 overflow-hidden hover:border-emerald-400 hover:shadow-sm transition-all bg-white"
                title="Click to enlarge certificate"
                data-testid="accreditation-modal-evidence-thumb"
              >
                <img
                  src={thumbnail || publicCert}
                  alt={`${a.short_label || 'Accreditation'} certificate`}
                  className="w-full max-h-64 object-contain bg-slate-50"
                  data-testid="accreditation-modal-evidence-image"
                />
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold py-1.5 text-center bg-emerald-50 border-t border-slate-200">
                  Click to enlarge
                </div>
              </button>
            )}
          </div>
        )}

        <div className="space-y-2 text-sm" data-testid="accreditation-modal-body">
          {issuer && (
            <div className="flex items-start gap-2" data-testid="accreditation-modal-issuer">
              <Building2 className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Issuing authority</p>
                <p className="text-slate-700">{issuer}</p>
              </div>
            </div>
          )}
          {expiry && (
            <div className="flex items-start gap-2" data-testid="accreditation-modal-expiry">
              <Calendar className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Valid until</p>
                <p className="text-slate-700">{fmtDate(expiry)}</p>
              </div>
            </div>
          )}
          {note && (
            <div className="flex items-start gap-2" data-testid="accreditation-modal-note">
              <Info className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Verification note</p>
                <p className="text-slate-600 text-xs">{note}</p>
              </div>
            </div>
          )}
          {thumbnail && !hasVisualEvidence && (
            <div className="pt-2" data-testid="accreditation-modal-thumbnail">
              <img
                src={thumbnail}
                alt={`${a.short_label || 'Accreditation'} preview`}
                className="rounded border border-slate-200 max-h-40 object-contain mx-auto"
              />
            </div>
          )}
          {publicCert && !hasVisualEvidence && (
            <div className="pt-2" data-testid="accreditation-modal-public-link">
              <a
                href={publicCert}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-emerald-700 hover:underline"
              >
                View public certificate ↗
              </a>
            </div>
          )}
          {!hasAnyPublicEvidence && (
            <div
              className="flex items-start gap-2 rounded-md bg-emerald-50 border border-emerald-100 p-3"
              data-testid="accreditation-modal-fallback"
            >
              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-emerald-900/80 leading-relaxed">
                Verified by TreatX compliance review. Detailed documentation is kept private under our compliance policy.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="accreditation-modal-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* SEARCH-UX-HARDFIX-02 FIX-1 — full-size lightbox for image
        certificates.  Same shadcn `Dialog` primitive used by
        DoctorDetailModal — no parallel viewer subsystem. */}
    <Dialog open={lightboxOpen} onOpenChange={(v) => { if (!v) setLightboxOpen(false); }}>
      <DialogContent
        className="max-w-3xl p-2 bg-black/95 border-0"
        data-testid="accreditation-modal-lightbox"
      >
        <DialogHeader className="px-2">
          <DialogTitle className="text-white text-sm" data-testid="accreditation-modal-lightbox-title">
            {a.long_label || a.short_label || 'Accreditation certificate'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Enlarged accreditation certificate preview
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center bg-black/40 rounded-md overflow-hidden">
          <img
            src={publicCert || thumbnail}
            alt={`${a.short_label || 'Accreditation'} certificate (enlarged)`}
            className="max-h-[78vh] max-w-full object-contain"
            data-testid="accreditation-modal-lightbox-image"
          />
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
